import React, {useContext, useRef, useState} from 'react';
import axios from 'axios';
import config from '../../../../config';
import UserContext from '../../../common/UserContext';

// reactstrap
import {Col, Row} from 'reactstrap';

// components
import DialogLayout from "./DialogLayout";
import {toHtml} from '../../utilities'

// kendo react
import {Label} from '@progress/kendo-react-labels';
import {TextArea} from '@progress/kendo-react-inputs';
import {Popover} from '@progress/kendo-react-tooltip';

// multilingual
import {
    hostIdFormattingKey,
    mainMessages,
} from "../../../../assets/text/MultilingualText";
import {useLocalization} from "@progress/kendo-react-intl";
import KSMPopover from "../../Popovers";
import {InfoIcon} from "../../icons";
import {saveHostToProfile} from "./utilities";


function MultiHostModal(props) {
    const {
        setIsLoading,
        hostDetails,
        assignedProducts,
        selectedProducts,
        onePerHost = 'redeem_one_per_host',
        productNum = 'product_number',
        productID = 'product_id',
        qtyAvailable = 'qty_available',
        qtyRequested =  'qty_requested',
        selectID = 'selected',
        errors,
        setErrors,
        isValid,
        setIsValid,
        showError,
        createHost,
        resetSelectedProducts,
        closeModal,
        savedHostInfo,
        saveHostMethod = null,
        children,
    } = props;
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const localization = useLocalization();

    let hostIDType = hostDetails.host_id_type || ""
    let hostIDLabel = hostDetails.host_id_info.asl_prompt || "";
    let hostIDHint = hostDetails.host_id_info.input_hint || "";
    let hostIDPatterns = hostDetails.host_id_info.patterns || [];

    const hostIDPopover = useRef(null);
    const [multiHost, setMultiHost] = useState("");

    const [isVisible, setIsVisible] = useState({
        hostIDHint: false,
    })

    /*
     * validateMultiHost() validates the user multiple hosts or imei's input on the frontend prior to validating the
     * host id || serial number input on the backend
    */
    const validate = () => {
        setErrors([]);
        setIsValid({
            multiHost: true,
        });

        let invalidMultiHosts = [];
        let multiHostData = multiHost.length ? multiHost.split('\n').filter(host => host) : [];

        multiHostData.forEach((host) => {
            // isValidHost is true if the host matches all regex pattern in hostIDPatterns
            let isValidHost = hostIDPatterns.some(regex => regex.test(host.trim()));
            if (!isValidHost) invalidMultiHosts.push(host.trim());
        });

        if (multiHostData.length && !invalidMultiHosts.length) {
            validateHostRegEx();
        } else { // show error message for no host or any invalid host
            showError('multiHost', invalidMultiHosts);
        }
    }

    /*
     * validateHostRegEx() validates the user host id || serial number input on the backend
    */
    const validateHostRegEx = () => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let host_or_imei_values = [];
        host_or_imei_values = multiHost.split('\n').filter(host => host)
        host_or_imei_values.forEach((host, idx) => {
            return host_or_imei_values[idx] = host.trim()
        })

        let data = {
            "host_id_type": hostIDType,
            "host_or_imei_values": host_or_imei_values,
            "serial_id_value": ""
        };

        setIsLoading(true);

        axios.post(
            config.request_license.HOST_VALIDATION_REGEX,
            data,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    setErrors([]);
                    if (saveHostMethod === null) {
                        saveHostToProfile(
                            accessToken,
                            setIsLoading,
                            showError,
                            saveHostID,
                            host_or_imei_values,
                            savedHostInfo,
                            hostDetails
                        )
                    } else {
                        saveHostMethod(
                            accessToken,
                            setIsLoading,
                            showError,
                            saveHostID,
                            host_or_imei_values,
                            savedHostInfo,
                            hostDetails
                        )
                    }
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to POST Host Validation Regex", error);
                let status = error?.response?.status

                if (status === 400) {
                    let invalidMultiHosts = error.response.data.error_detail ? error.response.data.error_detail : [];
                    showError('multiHost', invalidMultiHosts);
                } else if (status === 400 && error?.response?.data?.error_code === "MOD_HSVAL_215") {
                    showError('deniedHost', error?.response?.data?.error_detail)
                } else {
                    showError('generic')
                }
                setIsLoading(false);
            })
    }


    // Find duplicates in array
    const findDuplicates = arr => arr.filter((e, i) => arr.indexOf(e) !== i);

    /*
     * saveHostID() saves the assigned host
    */
    const saveHostID = (savedData = null) => {
        // format multiple hosts or imei's to list of string
        let multiHostData = multiHost.length ? multiHost.split('\n').filter(host => host) : [];
        multiHostData.forEach((host, idx) => {
            return multiHostData[idx] = host.trim()
        })

        // case: find duplication of host ids
        let duplicateHosts = findDuplicates(multiHostData);
        if (duplicateHosts.length) {
            showError("duplicateHost", duplicateHosts);
            setIsLoading(false)
            return null
        }

        // case: check if qty can be evenly distributed to all hosts
        let qtyErrors = [];
        let hostCount = multiHostData.length;
        selectedProducts.forEach((item) => {
            if (item[selectID]) {
                let qty = item[qtyAvailable];
                let assignQty = item[qtyRequested];
                let distribution = assignQty * hostCount;

                // check if there is enough qty to be distributed
                // and the product's qty will be evenly distributed across each host
                if (!(qty - distribution >= 0 && distribution % hostCount === 0)) {
                    qtyErrors.push({
                        "product": item.product_number,
                        "qty": qty,
                        "qty_needed": hostCount * assignQty,
                        "assign_qty": assignQty,
                        "host_count": hostCount
                    })
                }
            }
        });
        if (qtyErrors.length) {
            showError("qtyDistribution", qtyErrors);
            setIsLoading(false);
            return null
        }

        // case: products that are 1 per host can only be assigned to a single host
        let foundOnePerHostError = false
        multiHostData.forEach((multiHost) => {
            const hostId = multiHost;
            const host = assignedProducts.find(host =>
                host.host_id &&
                hostId &&
                host.host_id === hostId
            )

            if (host) {
                let unAssignable = [];
                selectedProducts.forEach((item) => {
                    if (item[selectID]) {
                        let product = host.products.find(product => item[productID] === product[productID]);
                        if (item[onePerHost].toUpperCase() === "Y" && product) {
                            unAssignable.push(product[productNum]);
                        }
                    }
                })

                if (unAssignable.length > 0) {
                    unAssignable.forEach((item) => {
                        showError('onePerHost', item);
                    })
                    foundOnePerHostError = true
                }
            }
        })
        if (foundOnePerHostError) {
            setIsLoading(false);
            return null
        }

        multiHostData.forEach((multiHost) => {
            const hostId = multiHost;
            const panelBarTitle = "Host: " + multiHost;
            const host = assignedProducts.find(host =>
                host.host_id &&
                hostId &&
                host.host_id === hostId
            )
            createHost(panelBarTitle, host, hostId, "", null, savedData)
        })
        resetSelectedProducts(hostCount)
    }


    return (
        <DialogLayout
            closeModal={closeModal}
            productTable={children}
            errors={errors}
            validate={validate}
        >
            <Row>
                <Col>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.2rem',
                        lineHeight: '2rem'
                    }}>
                        <Label>{toHtml(hostIDLabel)}</Label>
                        <div>
                            <InfoIcon
                                onClick={() => {
                                    setIsVisible(isVisible => ({
                                        ...isVisible,
                                        hostIDHint: !isVisible.hostIDHint,
                                    }));
                                }}
                                size={"medium"}
                                style={{
                                    color: 'var(--keysight-secondary)',
                                    cursor: 'pointer',
                                    marginTop: '0.7rem',
                                }}
                            />
                        </div>
                    </div>
                    <KSMPopover
                        show={isVisible.hostIDHint}
                        setShowHandler={() => {
                            setIsVisible(isVisible => ({
                                ...isVisible,
                                hostIDHint: false,
                            }));
                        }}
                        anchor={hostIDPopover.current}
                        position={'right'}
                        type={'warning'}
                        style={{maxWidth: 600}}
                        message={
                            <>
                                <b style={{fontWeight: '600'}}>
                                    {localization.toLanguageString(hostIdFormattingKey, mainMessages[siteLanguageDefault][hostIdFormattingKey])}
                                </b>
                                <br/>
                                {toHtml(hostIDHint)}
                            </>
                        }
                    />

                    <div ref={hostIDPopover}>
                        <TextArea
                            value={multiHost}
                            rows={10}
                            autoSize={false}
                            onChange={(e) => {
                                setIsValid(isValid => ({
                                    ...isValid,
                                    multiHost: true
                                }));
                                setMultiHost(e.value);
                            }}
                            valid={isValid.multiHost}
                            autoFocus={true}
                        />
                    </div>
                </Col>
                <Col/>
            </Row>
        </DialogLayout>
    )
}

export default MultiHostModal;